import { type ReactNode, useEffect, useId, useRef } from "react";

export type ConfirmDialogProps = {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	title: string;
	description: string | ReactNode;
	confirmLabel?: string;
	cancelLabel?: string;
	destructive?: boolean;
	initialFocus?: "cancel" | "confirm";
	onConfirm: () => void | Promise<void>;
	isPending?: boolean;
};

/**
 * Accessible confirmation dialog built on the native <dialog> element.
 *
 * Design notes:
 * - Mirrors the `InviteDialog` pattern (see `26b6106 fix(web): InviteDialog
 *   a11y — native <dialog> with ESC, focus trap, auto-focus`) so both modals
 *   share the same keyboard + screen-reader contract.
 * - Role is `alertdialog` because every confirm interrupts the user and
 *   requires a decision — that's exactly WAI-ARIA's definition.
 * - Initial focus defaults to Cancel for destructive actions (safer default —
 *   pressing Enter accidentally won't nuke data) and Confirm for benign ones.
 * - Clicking the ::backdrop closes the dialog (matches the native `<dialog>`
 *   + `showModal()` behavior users encounter in the rest of the app).
 */
export function ConfirmDialog({
	open,
	onOpenChange,
	title,
	description,
	confirmLabel = "Confirm",
	cancelLabel = "Cancel",
	destructive = false,
	initialFocus,
	onConfirm,
	isPending = false,
}: ConfirmDialogProps) {
	const dialogRef = useRef<HTMLDialogElement | null>(null);
	const cancelRef = useRef<HTMLButtonElement | null>(null);
	const confirmRef = useRef<HTMLButtonElement | null>(null);
	const titleId = useId();
	const descriptionId = useId();

	// Destructive actions get Cancel as the safe default; everything else
	// gets Confirm so Enter progresses the flow.
	const resolvedInitialFocus = initialFocus ?? (destructive ? "cancel" : "confirm");

	// Sync the imperative <dialog> API with declarative `open` prop and wire
	// the keyboard ESC (cancel event) + mouse backdrop-click closures back to
	// the parent's state setter. Both listeners go through addEventListener
	// rather than JSX handlers — the backdrop affordance is mouse-only by
	// design (keyboard users get ESC), so the JSX `onClick` would just trip
	// `useKeyWithClickEvents` for no real a11y gain.
	useEffect(() => {
		const dlg = dialogRef.current;
		if (!dlg) return;

		if (open) {
			if (!dlg.open) dlg.showModal();
			const focusTarget =
				resolvedInitialFocus === "cancel" ? cancelRef.current : confirmRef.current;
			focusTarget?.focus();
		} else if (dlg.open) {
			dlg.close();
		}

		function handleCancel(e: Event) {
			// Prevent the default close; let React own the state transition so
			// `onOpenChange(false)` fires exactly once and the parent can react
			// (e.g. clear pending state) before the DOM goes.
			e.preventDefault();
			onOpenChange(false);
		}

		function handleBackdropClick(e: MouseEvent) {
			if (isPending) return;
			if (e.target === dlg) onOpenChange(false);
		}

		dlg.addEventListener("cancel", handleCancel);
		dlg.addEventListener("click", handleBackdropClick);
		return () => {
			dlg.removeEventListener("cancel", handleCancel);
			dlg.removeEventListener("click", handleBackdropClick);
		};
	}, [open, onOpenChange, resolvedInitialFocus, isPending]);

	async function handleConfirm() {
		await onConfirm();
	}

	// R18: migrated from raw `bg-[#hex]` to the `brand-*` semantic tokens
	// defined in `styles.css`. Colors are visually identical.
	const confirmClasses = destructive
		? "h-9 rounded bg-brand-destructive px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-destructive-hover disabled:opacity-50"
		: "h-9 rounded bg-brand-primary px-4 text-sm font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:opacity-50";

	return (
		<dialog
			ref={dialogRef}
			role="alertdialog"
			aria-labelledby={titleId}
			aria-describedby={descriptionId}
			className="w-full max-w-md rounded-lg border border-[#e5e7eb] bg-white p-6 shadow-xl backdrop:bg-black/40"
		>
			<div>
				<h3 id={titleId} className="text-base font-semibold text-[#1e1e20]">
					{title}
				</h3>
				<div id={descriptionId} className="mt-2 text-sm text-[#6b7280]">
					{description}
				</div>
				<div className="mt-5 flex items-center justify-end gap-2">
					<button
						ref={cancelRef}
						type="button"
						onClick={() => onOpenChange(false)}
						disabled={isPending}
						className="h-9 rounded border border-[#d1d5db] bg-white px-3 text-sm font-medium text-[#6b7280] hover:bg-[#f8f9fb] disabled:opacity-50"
					>
						{cancelLabel}
					</button>
					<button
						ref={confirmRef}
						type="button"
						onClick={handleConfirm}
						disabled={isPending}
						className={confirmClasses}
					>
						{isPending ? (
							<span className="inline-flex items-center gap-2">
								<span
									aria-hidden="true"
									className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white"
								/>
								{confirmLabel}
							</span>
						) : (
							confirmLabel
						)}
					</button>
				</div>
			</div>
		</dialog>
	);
}
